import type { WorldConnection } from '../net/connection';
import { SK, btnCss, panelCss, titleCss, redHover, injectSkinCss } from './skin';

// ADMIN PANEL (backtick, owner only). Two things in one screen: DEV TOOLS (fly / god / unlimited resources --
// the last covers free build + free craft + bottomless ammo) as header toggles, and a searchable roster of
// every player the server knows -- online dot, country, ping, position -- with per-row teleport / kick / ban /
// mute actions. Every moderation action is a socket message the SERVER re-authorizes against the token→owner
// check, so this panel is only a convenience surface; a non-admin who forged it open could do nothing.

type Row = { acct: string; name: string; online: boolean; country: string; ping: number; x: number; z: number; admin?: boolean; banned?: number; muted?: boolean };
type Toggle = { label: string; get: () => boolean; toggle: () => void };
const KICKS: Array<[string, number]> = [['5m', 300000], ['30m', 1800000], ['1h', 3600000], ['12h', 43200000], ['24h', 86400000]];

export class ModPanel {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly searchInput: HTMLInputElement;
  private open_ = false;
  private rows: Row[] = [];
  private query = '';
  private poll = 0;
  private readonly toolBtns: Array<{ t: Toggle; el: HTMLButtonElement }> = [];

  constructor(private readonly conn: WorldConnection, private readonly tools?: { fly: Toggle; god: Toggle; res: Toggle }) {
    injectSkinCss();
    this.root = document.createElement('div');
    this.root.dataset.ui = '1';
    this.root.style.cssText = css('position:fixed', 'inset:0', 'z-index:40', 'display:none', 'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`, `font:${SK.fsBody} ${SK.font}`, `color:${SK.text}`);
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });
    const panel = document.createElement('div');
    panel.style.cssText = css(panelCss('glass'), 'display:flex', 'flex-direction:column', 'gap:12px', 'padding:20px 24px', 'width:min(880px,94vw)', 'max-height:86vh',
      'box-shadow:0 24px 70px rgba(0,0,0,0.7)');

    // Header: title, the dev-tool toggles, and a close button.
    const header = document.createElement('div');
    header.style.cssText = css('display:flex', 'align-items:center', 'gap:8px', 'flex-wrap:wrap');
    const title = document.createElement('div');
    title.textContent = 'ADMIN';
    title.style.cssText = css(titleCss(), 'margin-right:8px');
    header.appendChild(title);
    for (const t of [this.tools?.fly, this.tools?.god, this.tools?.res].filter(Boolean) as Toggle[]) {
      const b = document.createElement('button');
      b.style.cssText = btnCss('secondary', true); // paintTools recolors per state
      b.onclick = () => { t.toggle(); this.paintTools(); };
      header.appendChild(b);
      this.toolBtns.push({ t, el: b });
    }
    const spacer = document.createElement('div'); spacer.style.cssText = 'flex:1'; header.appendChild(spacer);
    const close = document.createElement('button');
    close.textContent = '✕ Close';
    close.style.cssText = btnCss('secondary', true);
    close.onclick = () => this.close();
    redHover(close);
    header.appendChild(close);

    // Search box (filters the roster by name).
    this.searchInput = document.createElement('input');
    this.searchInput.placeholder = 'Search players…';
    this.searchInput.style.cssText = css('padding:8px 12px', `font:${SK.fsBody} ${SK.font}`, `background:${SK.well}`, `color:${SK.text}`, `border:1px solid ${SK.border}`, `border-radius:${SK.rSm}`, 'outline:none');
    this.searchInput.addEventListener('input', () => { this.query = this.searchInput.value.toLowerCase(); this.render(); });

    this.body = document.createElement('div');
    this.body.style.cssText = css('overflow:auto', 'display:flex', 'flex-direction:column', 'gap:4px');
    panel.appendChild(header); panel.appendChild(this.searchInput); panel.appendChild(this.body);
    this.root.appendChild(panel);
    document.body.appendChild(this.root);
    this.conn.onModRoster = (players) => { this.rows = players as Row[]; if (this.open_) this.render(); };
  }

  get isOpen(): boolean { return this.open_; }
  toggle(): void { this.open_ ? this.close() : this.show(); }

  show(): void {
    this.open_ = true;
    this.root.style.display = 'flex';
    try { document.exitPointerLock(); } catch { /* not locked */ }
    this.conn.requestModRoster();
    this.poll = window.setInterval(() => this.conn.requestModRoster(), 3000); // live-ish refresh while open
    this.paintTools();
    this.render();
  }
  close(): void {
    this.open_ = false;
    this.root.style.display = 'none';
    if (this.poll) { clearInterval(this.poll); this.poll = 0; }
  }

  // Reflect each dev-tool toggle's on/off state in its button (green = state-positive: ON; off is neutral).
  private paintTools(): void {
    for (const { t, el } of this.toolBtns) {
      const on = t.get();
      el.textContent = (on ? '⚡ ' : '') + t.label + ': ' + (on ? 'ON' : 'OFF');
      el.style.color = on ? SK.good : SK.dim;
      el.style.background = on ? SK.goodSoft : 'transparent';
      el.style.borderColor = on ? SK.good : SK.borderStrong;
    }
  }

  private render(): void {
    this.body.replaceChildren();
    const shown = this.rows.filter((r) => !this.query || r.name.toLowerCase().includes(this.query))
      .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
    if (!shown.length) { const e = document.createElement('div'); e.textContent = this.rows.length ? 'No match.' : 'No players yet…'; e.style.cssText = css('padding:16px', `color:${SK.dim}`); this.body.appendChild(e); return; }
    for (const r of shown) this.body.appendChild(this.rowEl(r));
  }

  private rowEl(r: Row): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'ox-hover-row';
    row.style.cssText = css('display:flex', 'align-items:center', 'gap:8px', 'padding:8px 12px', `border-radius:${SK.rSm}`,
      `background:${r.admin ? SK.surface2 : SK.raised}`, `border:1px solid ${SK.border}`, 'flex-wrap:wrap');
    const dot = document.createElement('span');
    dot.style.cssText = css('width:9px', 'height:9px', 'border-radius:50%', 'flex:none', `background:${r.online ? SK.good : SK.faint}`, r.online ? `box-shadow:0 0 6px ${SK.good}` : '');
    const nm = document.createElement('span');
    nm.textContent = r.name + (r.admin ? ' ★' : '');
    nm.style.cssText = css('min-width:120px', 'font-weight:600', `color:${SK.textHi}`);
    const meta = document.createElement('span');
    meta.textContent = `${r.country}  ·  ${r.ping >= 0 ? r.ping + 'ms' : '—'}  ·  (${r.x},${r.z})${r.banned ? '  · BANNED' : ''}${r.muted ? '  · MUTED' : ''}`;
    meta.style.cssText = css('flex:1', 'min-width:150px', `color:${r.banned ? SK.bad : SK.dim}`, `font-size:${SK.fsMicro}`, 'font-variant-numeric:tabular-nums');
    row.appendChild(dot); row.appendChild(nm); row.appendChild(meta);
    if (r.admin) return row; // never show moderation buttons against another admin (or yourself)

    const act = document.createElement('div');
    act.style.cssText = css('display:flex', 'gap:4px', 'flex-wrap:wrap');
    const btn = (label: string, kind: 'secondary' | 'danger' | 'confirm', fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText = btnCss(kind, true);
      if (kind === 'secondary') redHover(b); // semantic outlines keep their color on hover
      b.onclick = () => { fn(); };
      return b;
    };
    if (r.online) {
      act.appendChild(btn('TP to', 'secondary', () => this.conn.sendMod('tpTo', r.acct)));
      act.appendChild(btn('TP here', 'secondary', () => this.conn.sendMod('tpHere', r.acct)));
    }
    // Timed kicks (temp bans).
    const kick = document.createElement('select');
    kick.style.cssText = css('height:28px', 'padding:0 6px', `font:700 ${SK.fsMicro} ${SK.font}`, `background:${SK.surface2}`, `color:${SK.text}`, `border:1px solid ${SK.borderStrong}`, `border-radius:${SK.rMd}`, 'cursor:pointer');
    kick.appendChild(new Option('Kick…', ''));
    for (const [lbl, ms] of KICKS) kick.appendChild(new Option(lbl, String(ms)));
    kick.onchange = () => { if (kick.value) { this.conn.sendMod('kick', r.acct, Number(kick.value)); kick.value = ''; } };
    act.appendChild(kick);
    act.appendChild(btn(r.muted ? 'Unmute' : 'Mute', 'secondary', () => this.conn.sendMod(r.muted ? 'unmute' : 'mute', r.acct)));
    if (r.banned) act.appendChild(btn('Unban', 'confirm', () => this.conn.sendMod('unban', r.acct)));
    else {
      act.appendChild(btn('Ban server', 'danger', () => this.conn.sendMod('ban', r.acct)));
      act.appendChild(btn('Ban GAME', 'danger', () => { if (confirm(`Ban ${r.name} from the ENTIRE game?`)) this.conn.sendMod('gameban', r.acct); }));
    }
    row.appendChild(act);
    return row;
  }
}

function css(...lines: string[]): string { return lines.filter(Boolean).join(';'); }
